import { Component, Injector, Input, OnInit, ViewChild } from '@angular/core';
import { AppBaseComponent } from '../../app-base.component';
import { EvaluationDto, EvaluationService, EvaluationState, EvaluationType } from '@proxy/evaluations';
import { LazyLoadEvent } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import { Paginator } from 'primeng/paginator';
import { appModuleAnimation } from 'src/app/shared/animations/routerTransition';
import { DatePipe } from '@angular/common';
import { AppConsts } from '../../AppConsts';
import { CalibrationService } from '@proxy/calibrations';
import { AppPathConsts } from '../../AppPathConsts';
import { Router } from '@angular/router';
import { EvaluationGridModel } from './evaluation-grid-model';

@Component({
  selector: 'app-evaluations-grid',
  templateUrl: './evaluations-grid.component.html',
  animations: [appModuleAnimation()],
  providers: [DatePipe],
  standalone: false,
})
export class EvaluationsGridComponent extends AppBaseComponent implements OnInit {

  @ViewChild('paginator') paginator: Paginator;
  
  @Input() evaluationGridModel: EvaluationGridModel;

  constructor(
    injector: Injector,
    private readonly _evaluationService: EvaluationService,
    private readonly _calibrationService: CalibrationService,
    private readonly router: Router,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.getEvaluations();
  }

  async getEvaluations(event?: LazyLoadEvent): Promise<void> {
    try {
      this.showLoading();

      const maxResultCount = this.isDefinedAndStrictlyPositiveNumber(this.primengTableHelper.getMaxResultCount(this.paginator, event)) ? this.primengTableHelper.getMaxResultCount(this.paginator, event) : AppConsts.DefaultMaxResultCount;

      const endpointToCall = this.evaluationGridModel?.isCalibrationGrid ? this._calibrationService.getEvaluationsForCalibrationByInputAndIsCurrentUserEvaluator : this._evaluationService.getAllEvaluationsByInputAndIsCurrentUserEvaluator;

      const result = await firstValueFrom(endpointToCall(
        {
          skipCount: this.primengTableHelper?.getSkipCount(this.paginator, event),
          maxResultCount: maxResultCount,
          evaluateeId: this.evaluationGridModel?.evaluateeId,
          evaluateeName: this.evaluationGridModel?.evaluateeNameFilter,
          state: this.evaluationGridModel?.selectedState,
          startDate: this.evaluationGridModel?.startDate,
          endDate: this.evaluationGridModel?.endDate,
          areaId: this.evaluationGridModel?.userAreaId,
          subAreaId: this.evaluationGridModel?.userSubAreaId,
          shiftId: this.evaluationGridModel?.selectedShift?.id,
          types: this.evaluationGridModel?.selectedEvaluationTypes,
          isOperatorEvaluationSummaryFlow: this.evaluationGridModel?.isOperatorEvaluationSummaryFlow ?? false
        },
        this.evaluationGridModel?.hasEvaluatorPermission ?? false
      ));

      this.primengTableHelper.records = result.items;
      this.primengTableHelper.totalRecordsCount = result.totalCount;

    } finally {
      this.hideLoading();
    }
  }

  isEvaluationActive(state : EvaluationState) : boolean {
    return state !== EvaluationState.Finished;
  }

  canEditEvaluation(evaluation: EvaluationDto): boolean {
    if (evaluation?.type === EvaluationType.Autoevaluation) {
      return this.evaluationGridModel?.canAccessSelfEvaluations ?? false;
    }

    return this.evaluationGridModel?.canEditEvaluations ?? this.evaluationGridModel?.hasEvaluatorPermission ?? false;
  }

  onEditEvaluation(evaluationInstanceId: number): void {
    if (this.evaluationGridModel?.isCalibrationGrid) {
      this.router.navigate([`${AppPathConsts.CalibrationPrefix}/${AppPathConsts.Update}${evaluationInstanceId}`]);
    } else {
      this.router.navigate([`${AppPathConsts.EvaluationPrefix}/${AppPathConsts.Update}${evaluationInstanceId}`]);
    }
  }

  onViewEvaluation(evaluationInstanceId: number): void {
    if (this.evaluationGridModel?.isCalibrationGrid) {
      this.router.navigate([`${AppPathConsts.CalibrationPrefix}/${evaluationInstanceId}`]);
    } else {
      this.router.navigate([`${AppPathConsts.EvaluationPrefix}/${evaluationInstanceId}`]);
    }
  }
}
